import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';

import { GenericPlantingQuestionsWithStatus, ProcessedAnswers } from 'src/app/tga/shared/models/models';
import { selectGenericPlantingQuestions } from 'src/app/tga/store/tga.selectors';

import {
  TGACheckboxQuestion,
  TgaInputType,
  TGAQuestion,
  TGARadioButtonQuestion,
} from '../../../models/question-models';

export interface ImprovementTopicsResult {
  hasKnolzettingStengelaantalIssue: boolean;
  hasUitgroeiKnollenIssue: boolean;
  hasNEfficiencyIssue: boolean;
  hasTemperatuurDroogtestressIssue: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class GenericPlantingQuestionsService {
  readonly store = inject(Store);

  public static readonly improvementTopicsKey = 'improvementTopics';
  public readonly longStorageExpectedKey = 'longStorageExpected';

  private readonly hasKnolzettingStengelaantalIssue = 'HasKnolzettingStengelaantalIssue';
  private readonly hasUitgroeiKnollenIssue = 'HasUitgroeiKnollenIssue';
  private readonly hasNEfficiencyIssue = 'HasNEfficiencyIssue';
  private readonly hasTemperatuurDroogtestressIssue = 'HasTemperatuurDroogtestressIssue';

  private tgaState$: Observable<GenericPlantingQuestionsWithStatus | null> = of();
  public plantingQuestions: TGAQuestion<TgaInputType>[] = [];

  processImprovementTopics(processedAnswers: ProcessedAnswers): ImprovementTopicsResult {
    const answer = processedAnswers[GenericPlantingQuestionsService.improvementTopicsKey];
    let selectedKeys: string[] = [];

    if (Array.isArray(answer) && answer.length > 0 && typeof answer[0] === 'object' && 'key' in answer[0]) {
      const checkboxArray = answer as { key: string; selected: boolean }[];
      selectedKeys = checkboxArray.filter((item) => item.selected === true).map((item) => item.key);
    } else if (Array.isArray(answer) && answer.length > 0 && typeof answer[0] === 'string') {
      selectedKeys = answer as string[];
    }

    return {
      hasKnolzettingStengelaantalIssue: selectedKeys.includes(this.hasKnolzettingStengelaantalIssue),
      hasUitgroeiKnollenIssue: selectedKeys.includes(this.hasUitgroeiKnollenIssue),
      hasNEfficiencyIssue: selectedKeys.includes(this.hasNEfficiencyIssue),
      hasTemperatuurDroogtestressIssue: selectedKeys.includes(this.hasTemperatuurDroogtestressIssue),
    };
  }

  initializePlantingQuestions(): void {
    this.getQuestions().subscribe((questions) => {
      this.plantingQuestions = questions;
    });
  }

  initValues(): TGAQuestion<TgaInputType>[] {
    this.tgaState$ = this.store.select(selectGenericPlantingQuestions);
    this.tgaState$.subscribe((withStatus) => {
      const state = withStatus?.genericPlanting;
      if (state) {
        for (const question of this.plantingQuestions) {
          if (question.key === GenericPlantingQuestionsService.improvementTopicsKey) {
            const improvementTopics = [
              { key: this.hasKnolzettingStengelaantalIssue, selected: state.hasKnolzettingStengelaantalIssue ?? false },
              { key: this.hasUitgroeiKnollenIssue, selected: state.hasUitgroeiKnollenIssue ?? false },
              { key: this.hasNEfficiencyIssue, selected: state.hasNEfficiencyIssue ?? false },
              { key: this.hasTemperatuurDroogtestressIssue, selected: state.hasTemperatuurDroogtestressIssue ?? false },
            ];
            question.value = improvementTopics;
          } else if (question.key === this.longStorageExpectedKey) {
            if (state.longStorageExpected !== null && state.longStorageExpected !== undefined) {
              question.value = String(state.longStorageExpected);
            } else {
              question.value = 'null';
            }
          }
        }
      }
    });
    return this.plantingQuestions;
  }

  getQuestions(): Observable<TGAQuestion<TgaInputType>[]> {
    return of([
      new TGACheckboxQuestion({
        key: GenericPlantingQuestionsService.improvementTopicsKey,
        label: 'TGA.Questionnaire.PlantingQuestions.ImprovementTopics.Question',
        required: false,
        order: 1,
        options: [
          {
            key: this.hasKnolzettingStengelaantalIssue,
            value: 'TGA.Questionnaire.PlantingQuestions.ImprovementTopics.Options.KnolzettingStengelaantal',
          },
          {
            key: this.hasUitgroeiKnollenIssue,
            value: 'TGA.Questionnaire.PlantingQuestions.ImprovementTopics.Options.UitgroeiKnollen',
          },
          {
            key: this.hasNEfficiencyIssue,
            value: 'TGA.Questionnaire.PlantingQuestions.ImprovementTopics.Options.NEfficiency',
          },
          {
            key: this.hasTemperatuurDroogtestressIssue,
            value: 'TGA.Questionnaire.PlantingQuestions.ImprovementTopics.Options.TemperatuurDroogtestress',
          },
        ],
      }),
      new TGARadioButtonQuestion({
        key: this.longStorageExpectedKey,
        label: 'TGA.Questionnaire.PlantingQuestions.LongStorageExpected.Question',
        required: true,
        order: 2,
        options: [
          { key: 'true', value: 'TGA.Questionnaire.PlantingQuestions.LongStorageExpected.Yes' },
          { key: 'false', value: 'TGA.Questionnaire.PlantingQuestions.LongStorageExpected.No' },
          { key: 'null', value: 'TGA.Questionnaire.PlantingQuestions.LongStorageExpected.Unknown' },
        ],
      }),
    ]);
  }
}
